import React, {useEffect, useState} from 'react'
import MemberRepo from '../../../Repo/MemberRepo'
import {NavigatorForMemberPage} from '../../../Navigator/NavigatorForMemberPage'
import {SearchPostCode} from '../../../../Common/SearchPostCode'
import {
  validateMemberAddressInput,
  ValidationAddressInput,
} from '../../../Utils/Validations/ValidateMembersAddressInput'
import {useAsync} from 'react-use'
import {useLocation} from 'react-router-dom'
import {MemberEditAddressInfo} from '../../../Props/MembersEditAddressPageLocationStates'
import {MBMemberError} from '../../../Common/MBMemberError'
import {Nullable} from '../../../../Common/TypeHelper'
import {animateScroll} from 'react-scroll'
import {PageTitle} from '../../Molecules/BaseMembersPages/PageTitle'
import StepGuide from '../../Molecules/StepGuide/StepGuide'
import {MessageForAction} from '../../Molecules/BaseMembersPages/MessageForAction'
import {MembersPageTemplate} from '../../Molecules/BaseMembersPages/MembersPageTemplate'
import {PIMSecondaryButton} from '../../Atoms/Buttons/Bases/PIMSecondaryButton'
import {PIMSubmitButton} from '../../Atoms/Buttons/Bases/PIMSubmitButton'
import {PIMPageActions} from '../../Atoms/Layouts/PIMPageActions'
import {PIMStack} from '../../Atoms/Layouts/Bases/PIMStack'
import {PIMCard} from '../../Atoms/Cards/PIMCard'
import {Spacer} from '../../../../Common/Components/Spacer'
import {InputErrorSummary} from '../../Molecules/ErrorsAndAlert/ErrorSummary/InputErrorSummary'
import {memberResponseStatusCheck} from '../../../Utils/MemberResponseStatusCheck'
import {ContactAddressInputContents} from '../../Components/InputContentBlocks/AddressInputs/ContactAddressInputContents'

type Props = {
  navigator: NavigatorForMemberPage
  memberRepo: MemberRepo
  searchPostCode: SearchPostCode
}

type ErrorState = {
  isInputError: boolean
  postCodeErrors: string[]
  addressPrefectureCodeErrors: string[]
  addressCityErrors: string[]
  addressStreetErrors: string[]
  addressBuildingErrors: string[]
  phoneMobileErrors: string[]
  phoneTelErrors: string[]
}

export function MembersEditAddressInputPage(props: Props): Nullable<JSX.Element> {
  const location = useLocation<MemberEditAddressInfo>()
  const locationState: MemberEditAddressInfo = location.state

  const [state, setState] = useState<MemberEditAddressInfo>({
    postCode: locationState?.postCode || '',
    addressPrefectureCode: locationState?.addressPrefectureCode || '',
    addressCity: locationState?.addressCity || '',
    addressStreet: locationState?.addressStreet || '',
    addressBuilding: locationState?.addressBuilding || '',
    phoneMobile: locationState?.phoneMobile || '',
    phoneTel: locationState?.phoneTel || '',
  })
  const [errorState, setErrorState] = useState<ErrorState>({
    isInputError: false,
    postCodeErrors: [],
    addressPrefectureCodeErrors: [],
    addressCityErrors: [],
    addressStreetErrors: [],
    addressBuildingErrors: [],
    phoneMobileErrors: [],
    phoneTelErrors: [],
  })
  const [processing, setProcessing] = useState<boolean>(false)

  const result = useAsync(async () => {
    return await props.memberRepo.getSelf()
  })

  useEffect(() => {
    if (locationState) {
      return
    }
    if (!result.value) {
      return
    }
    setState((prevState) => ({
      ...prevState,
      postCode: result.value?.data.member?.postCode || '',
      addressPrefectureCode: result.value?.data.member?.addressPrefectureCode || '',
      addressCity: result.value?.data.member?.addressCity || '',
      addressStreet: result.value?.data.member?.addressStreet || '',
      addressBuilding: result.value?.data.member?.addressBuilding || '',
      phoneMobile: result.value?.data.member?.phoneMobile || '',
      phoneTel: result.value?.data.member?.phoneTel || '',
    }))
  }, [result.value])

  if (!locationState) {
    if (result.loading) {
      return null
    }

    if (result.error) {
      throw new MBMemberError(result.error.message)
    }

    if (!result.value) {
      throw new MBMemberError('UnknownError')
    }

    memberResponseStatusCheck(result.value.status)
  }

  function goToEditAddressConfirmPage(): void {
    setProcessing(true)

    const inputForValidation: ValidationAddressInput = {
      ...state,
      postCode: state.postCode.trim(),
      addressCity: state.addressCity.trim(),
      addressStreet: state.addressStreet?.trim(),
      addressBuilding: state.addressBuilding?.trim(),
      phoneMobile: state.phoneMobile?.trim(),
      phoneTel: state.phoneTel?.trim(),
    }
    setState((prevState) => ({
      ...prevState,
      ...inputForValidation,
    }))

    const validationResult = validateMemberAddressInput(inputForValidation)

    if (validationResult.hasError) {
      setErrorState({
        isInputError: true,
        postCodeErrors: validationResult.errors.postCode,
        addressPrefectureCodeErrors: validationResult.errors.addressPrefectureCode,
        addressCityErrors: validationResult.errors.addressCity,
        addressStreetErrors: validationResult.errors.addressStreet,
        addressBuildingErrors: validationResult.errors.addressBuilding,
        phoneMobileErrors: validationResult.errors.phoneMobile,
        phoneTelErrors: validationResult.errors.phoneTel,
      })
      setProcessing(false)
      animateScroll.scrollToTop()
      return
    }
    props.navigator.goToEditAddressConfirmPage(inputForValidation)
  }

  function goToAccountInformationPage(): void {
    props.navigator.goToAccountInformationPage()
  }

  return (
    <MembersPageTemplate>
      <PageTitle>連絡先情報変更入力</PageTitle>

      <InputErrorSummary display={errorState.isInputError}/>

      <StepGuide activity={'edit'} current={'enter'}/>

      <Spacer mb={'16px'}>
        <PIMStack alignItems={'center'}>
          <PIMCard
            headerProps={{title: '連絡先情報変更'}}
            testId={'block-forms'}
          >
            <ContactAddressInputContents
              activity={'edit'}
              searchPostCode={props.searchPostCode}
              contactAddress={state}
              contactAddressErrors={errorState}
              onChange={(address): void => {
                setState((prevState) => {
                  return {
                    ...prevState,
                    postCode: address.postCode,
                    addressPrefectureCode: address.addressPrefectureCode ?? '',
                    addressCity: address.addressCity ?? '',
                    addressStreet: address.addressStreet ?? '',
                    addressBuilding: address.addressBuilding ?? '',
                    phoneMobile: address.phoneMobile ?? '',
                    phoneTel: address.phoneTel ?? ``,
                  }
                })
              }}
            />
          </PIMCard>
        </PIMStack>
      </Spacer>

      <MessageForAction>
        上記の内容に間違いがなければ<br/>『変更内容の確認に進む』ボタンを押してください。
      </MessageForAction>

      <PIMPageActions>
        <PIMSubmitButton
          testId={'confirm-changes-button'}
          disabled={processing}
          onClick={(): void => {
            goToEditAddressConfirmPage()
          }}
        >
          変更内容の確認に進む
        </PIMSubmitButton>
        <PIMSecondaryButton onClick={goToAccountInformationPage}>
          登録情報に戻る
        </PIMSecondaryButton>
      </PIMPageActions>

    </MembersPageTemplate>
  )
}
